import { Component, ElementRef, OnInit, ViewChild } from "@angular/core";
import {
  AbstractControl,
  FormArray,
  FormControl,
  FormGroup,
  UntypedFormBuilder,
  UntypedFormGroup,
  Validators,
} from "@angular/forms";
import { UserState } from "src/app/states/user.state";
import {
  Store,
  Actions,
  ofActionSuccessful,
  ofActionErrored,
  Select,
} from "@ngxs/store";
import { Place } from "src/app/interface/user";
import { debounceTime, forkJoin, Observable } from "rxjs";
import { DataService } from "src/app/services/data.service";
import { ApiService } from "src/app/services/api.service";
import { AlertService } from "src/app/services/alert.service";
import {
  DeleteDeliveryPlace,
  UpdatePlace,
  UpdatePlaceDeliveriesCoordinates,
  UpdateSelectedPlace,
} from "src/app/states/actions/user.actions";
import Swal from "sweetalert2";
import { Activity, FileDto } from "src/app/interface/admin";
import { FilePlaceService } from "src/app/services/filePlace.service";
import { MatDialog } from "@angular/material/dialog";
import { SelectCoordinatesModalComponent } from "../select-coordinates-modal/select-coordinates-modal.component";
@Component({
  selector: "app-delivery",
  templateUrl: "./delivery.component.html",
  styleUrls: ["./delivery.component.scss"],
})
export class DeliveryComponent implements OnInit {
  @ViewChild("fileInput") fileInput!: ElementRef<HTMLInputElement>;
  map: any;
  L = window["L"];
  file: FileDto;
  marker: any;
  circle: any;
  placeInfo: UntypedFormGroup;
  data: Place = null;
  activities: Activity[] = [];
  filteredActivities: Activity[] = [];
  searchControl = new FormControl("");
  circles: any[] = [];
  form: FormGroup;
  lat: number;
  lng: number;
  nameAddress: string;
  mainMarker;

  @Select(UserState.defaultCoin) defaultCoin: Observable<any>;
  @Select(UserState.selectedPlace) selectedPlace: Observable<Place>;

  constructor(
    private fb: UntypedFormBuilder,
    private store: Store,
    private _dataService: DataService,
    private actions: Actions,
    private api: ApiService,
    private _alertService: AlertService,
    private _fileService: FilePlaceService,
    private dialog: MatDialog
  ) {}

  ngOnInit() {
    this._dataService.displayLoading();
    const { selectedPlace } = this.store.selectSnapshot(UserState);
    this.data = selectedPlace;
    this.initPlaceForm();

    this.defaultCoin.subscribe((coin) => {
      if (this.data?.Delivery?.length) {
        this.loadDeliveryForm(this.data.Delivery, coin);
        const dataDelivery = this.data.Delivery[0];
        this.lat = dataDelivery.Latitude;
        this.lng = dataDelivery.Longitude;
        this.nameAddress = dataDelivery.Address;
      } else {
        this.lat = 0;
        this.lng = 0;
        this.nameAddress = "Sin dirección";
        this.form = this.fb.group({
          deliveryPlaces: this.fb.array([]),
        });
      }
      this.initMap();
    });

    this.store.select(UserState.selectedPlace).subscribe((place) => {
      if (!place) return;

      this.data = place;
      this.initPlaceForm();

      if (place.Delivery?.length) {
        const coin = this.store.selectSnapshot(UserState.defaultCoin);
        this.loadDeliveryForm(place.Delivery, coin);

        const dataDelivery = place.Delivery[0];
        if (
          dataDelivery.Latitude !== this.lat ||
          dataDelivery.Longitude !== this.lng
        ) {
          this.lat = dataDelivery.Latitude;
          this.lng = dataDelivery.Longitude;
          this.nameAddress = dataDelivery.Address;
          this.refreshMap();
        }
      } else {
        this.lat = 0;
        this.lng = 0;
        this.nameAddress = "Sin dirección";
        this.refreshMap();
        this.form = this.fb.group({
          deliveryPlaces: this.fb.array([]),
        });
        this._dataService.displayLoading(false);
      }
    });
  }

  private loadDeliveryForm(deliveries, coin): void {
    const groups = deliveries.map((d) =>
      this.fb.group({
        id: [d.id],
        Price: [d.Price / coin.Buy_Coin],
        Radius: [d.Radius],
        Available: [d.Available],
        FK_Place: [this.data.id],
        FreeShipping: [d.FreeShipping / coin.Buy_Coin],
        ProfitPercentage: [d.ProfitPercentage],
      })
    );

    if (!this.form) {
      this.form = this.fb.group({
        deliveryPlaces: this.fb.array(groups),
      });
    } else {
      this.form.setControl("deliveryPlaces", this.fb.array(groups));
    }
  }

  trackByIndex(index: number, item: AbstractControl) {
    return index;
  }

  getColor(index: number): string {
    const hue = (index * 137.508) % 360;
    return `hsl(${hue}, 70%, 50%)`;
  }

  private initPlaceForm() {
    this.getActivities();
    let descriptionPlace = this.data.Description_Place || "";
    let namePlace = this.data.Name_Place || "";
    let sinpeNumber = this.data.Sinpe_Number || "";
    let code = this.data.Activity?.Name || "";

    this.placeInfo = this.fb.group({
      Description_Place: [descriptionPlace, [Validators.required]],
      Name_Place: [namePlace, [Validators.required]],
      Sinpe_Number: [sinpeNumber, [Validators.required]],
      Code: [code, [Validators.required]],
    });
  }

  initMap() {
    if (!document.getElementById("map")) {
      setTimeout(() => this.initMap(), 100);
      return;
    }
    this.L = window["L"];
    if (!this.L) {
      setTimeout(() => this.initMap(), 1000);
      return;
    }

    if (this.map) {
      this.map.remove();
      this.circles = [];
    }

    this.map = this.L.map("map").setView([this.lat, this.lng], 13);
    this.L.tileLayer("https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png", {
      maxZoom: 19,
      attribution:
        '&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a> contributors',
    }).addTo(this.map);

    const center = [this.lat, this.lng];

    this.mainMarker = this.L.marker(center)
      .addTo(this.map)
      .bindPopup("Centro de envío")
      .openPopup();

    if (this.deliveryPlaces.controls.length) {
      this.deliveryPlaces.controls.forEach((group, index) => {
        const radius = group.get("Radius").value;
        const color = this.getColor(index);

        const circle = this.L.circle(center, {
          color: color,
          fillColor: color,
          fillOpacity: 0.2,
          radius: radius,
        }).addTo(this.map);

        this.circles.push(circle);

        group.get("Radius").valueChanges.subscribe((newRadius) => {
          this.circles[index].setRadius(newRadius);
        });
      });
    }

    this._dataService.displayLoading(false);
  }

  openCoordinateModal() {
    const dialogRef = this.dialog.open(SelectCoordinatesModalComponent, {
      width: "600px",
      data: { currentLat: this.lat, currentLng: this.lng },
    });

    dialogRef.afterClosed().subscribe((result) => {
      if (result) {
        this._dataService.displayLoading();
        this.store.dispatch(
          new UpdatePlaceDeliveriesCoordinates(
            this.data.id,
            result.lat,
            result.lng,
            result.address
          )
        );
        this.lat = result.lat;
        this.lng = result.lng;
        this.nameAddress = result.address;

        setTimeout(() => {
          this.refreshMap();
          this.resubscribeToRadiusChanges();
        }, 500);
        this.handler(UpdatePlaceDeliveriesCoordinates);
      }
    });
  }

  get deliveryPlaces(): FormArray {
    return this.form.get("deliveryPlaces") as FormArray;
  }

  setRadius = (radius: number, index: number) => {
    if (this.circles && this.circles[index]) {
      this.circles[index].setRadius(radius);
    }
  };

  addDeliveryPlace() {
    this.defaultCoin.subscribe((coin) => {
      const group = this.fb.group({
        id: [null],
        Price: [0],
        Radius: [null],
        Available: [true],
        FK_Place: [this.data.id],
        FreeShipping: [0],
        ProfitPercentage: [0],
      });

      this.deliveryPlaces.push(group);
      const center = [this.lat, this.lng];

      if (this.map && center) {
        const color = this.getColor(this.deliveryPlaces.length - 1);
        const circle = this.L.circle(center, {
          color,
          fillColor: color,
          fillOpacity: 0.2,
          radius: group.get("Radius").value,
        }).addTo(this.map);

        this.circles.push(circle);

        group.get("Radius").valueChanges.subscribe((newRadius) => {
          circle.setRadius(newRadius);
        });
      }
    });
  }

  refreshMap() {
    if (!this.map) return;

    const newCenter = [this.lat, this.lng];
    this.map.setView(newCenter, 13);

    if (!this.mainMarker) {
      this.mainMarker = this.L.marker(newCenter).addTo(this.map);
    } else {
      this.mainMarker.setLatLng(newCenter);
    }

    this.circles.forEach((circle) => {
      circle.setLatLng(newCenter);
    });
  }

  save = async () => {
    const text = this.data.Delivery ? "actualizar" : "guardar";
    const result = await Swal.fire({
      title: "Confirmación",
      text: `¿Seguro que deseas ${text} los datos de envío?`,
      showCancelButton: true,
      confirmButtonText: "Confirmar",
      cancelButtonText: "Cancelar",
      reverseButtons: true,
      customClass: {
        confirmButton: "swal-confirm-button",
        cancelButton: "swal-cancel-button",
      },
    });

    if (!result.isConfirmed) return;

    this._dataService.displayLoading();
    const formArray = this.form.get("deliveryPlaces") as FormArray;
    const coin = this.store.selectSnapshot(UserState.defaultCoin);
    const deliveryList = formArray.value;

    const requests = deliveryList.map((params) => {
      const price = this.getFormattedAmount(Number(params.Price), coin);
      const freeShipping = this.getFormattedAmount(
        Number(params.FreeShipping),
        coin
      );

      const data = {
        id: Number(params.id),
        Price: price,
        Radius: Number(params.Radius),
        Available: Number(params.Available),
        FK_Place: this.data.id,
        FK_Coin: coin.id,
        FreeShipping: freeShipping,
        Latitude: this.lat,
        Longitude: this.lng,
        Address: this.nameAddress,
        ProfitPercentage: Number(params.ProfitPercentage),
      };

      return this.api.setPlaceDelivery(data);
    });

    forkJoin(requests).subscribe({
      next: (responses: any) => {
        const allOk = responses.every(
          (res) =>
            res.data.savePlaceDelivery && !res.data.savePlaceDelivery.error
        );

        if (!allOk) {
          this._alertService.openSnackBar(
            "Error al guardar alguno de los envíos.",
            "error"
          );
        } else {
          const deliveries = responses.map((res) => res.data.savePlaceDelivery);
          const newPlace = {
            id: this.data.id,
            Delivery: deliveries,
          };

          this.store.dispatch(new UpdatePlace(newPlace));
          this.handler(UpdatePlace);
          this.resubscribeToRadiusChanges();

          this._alertService.openSnackBar(
            "Se ha guardado la información con éxito.",
            "success"
          );
        }

        this._dataService.displayLoading(false);
      },
      error: (err) => {
        this._alertService.openSnackBar(
          "Error al guardar los datos de envío",
          "error"
        );
        this._dataService.displayLoading(false);
      },
    });
  };

  confirmDeleteDeliveryPlace(index: number, id: number) {
    const total = this.deliveryPlaces.controls.length;

    if (id > 0 && total === 1) {
      this._alertService.openSnackBar(
        "Debe mantener al menos un rango de envío.",
        "warn"
      );
      return;
    }
    Swal.fire({
      title: "Eliminar",
      text: "¿Estás seguro que deseas eliminar este rango de envío?",
      showCancelButton: true,
      confirmButtonText: "Eliminar",
      cancelButtonText: "Cancelar",
      reverseButtons: true,
      customClass: {
        confirmButton: "swal-confirm-button",
        cancelButton: "swal-cancel-button",
      },
    }).then((result) => {
      if (result.isConfirmed) {
        this.deleteDeliveryPlace(index, id);
      }
    });
  }

  deleteDeliveryPlace(index: number, id_Delivery: number) {
    if (!id_Delivery || id_Delivery === 0) {
      this.resubscribeToRadiusChanges();
      return;
    }

    this._dataService.displayLoading();

    this.api.setDeletePlaceDelivery(id_Delivery).subscribe({
      next: (res) => {
        if (res.data.deletePlaceDelivery) {
          const { id } = this.store.selectSnapshot(UserState.selectedPlace);

          this.store.dispatch(new DeleteDeliveryPlace(id, id_Delivery));
          this.resubscribeToRadiusChanges();
          this._alertService.openSnackBar(
            "El rango de envío fue eliminado.",
            "success"
          );
        } else {
          this._alertService.openSnackBar(
            "No se pudo eliminar. Intenta de nuevo.",
            "error"
          );
        }

        this._dataService.displayLoading(false);
      },
      error: () => {
        this._dataService.displayLoading(false);

        this._alertService.openSnackBar(
          "No se pudo eliminar. Intenta de nuevo.",
          "error"
        );
      },
    });
  }

  resubscribeToRadiusChanges() {
    const center = [this.lat, this.lng];

    this.circles.forEach((circle) => this.map.removeLayer(circle));
    this.circles = [];

    this.deliveryPlaces.controls.forEach((group, index) => {
      const radius = group.get("Radius").value;
      const color = this.getColor(index);

      const circle = this.L.circle(center, {
        color: color,
        fillColor: color,
        fillOpacity: 0.2,
        radius: radius,
      }).addTo(this.map);

      this.circles.push(circle);

      group.get("Radius").valueChanges.subscribe((newRadius) => {
        this.circles[index].setRadius(newRadius);
      });
    });
  }

  getFormattedAmount = (total, coin) => {
    if (total > 0) {
      return total * coin.Buy_Coin;
    }
    return 0;
  };

  handler = (action) => {
    this.actions.pipe(ofActionSuccessful(action)).subscribe(() => {
      this._dataService.displayLoading(false);
    });
    this.actions.pipe(ofActionErrored(action)).subscribe(() => {
      this._dataService.displayLoading(false);
    });
  };

  getActivities() {
    try {
      this.api.getAllActivities().subscribe((res) => {
        if (res?.data?.allActivities) {
          this.activities = res.data.allActivities;

          const activityId = this.data?.Activity?.id_MH;

          if (activityId) {
            const selectedActivity = this.activities.find(
              (a) => a.id_MH === activityId
            );

            if (selectedActivity) {
              this.activities = [
                selectedActivity,
                ...this.activities.filter((a) => a.id_MH !== activityId),
              ];
              this.placeInfo.controls["Code"].setValue(selectedActivity.id_MH);
            }
          }

          this.filteredActivities = [...this.activities];

          this.searchControl.valueChanges
            .pipe(debounceTime(300))
            .subscribe((search: string) => {
              this.filteredActivities = this.activities.filter((a) =>
                a.Name.toLowerCase().includes(search.toLowerCase())
              );
            });
        }
      });
    } catch (err) {
      this._alertService.openSnackBar(
        "Error al obtener las actividades.",
        "error"
      );
    }
  }
  stopKeyDown(event: KeyboardEvent) {
    event.stopPropagation();
  }

  onSelectOpened() {
    this.searchControl.setValue("");
    const selectedId = this.placeInfo.controls["Code"].value;
    const selectedActivity = this.activities.find(
      (a) => a.id_MH === selectedId
    );

    if (selectedActivity) {
      this.filteredActivities = [
        selectedActivity,
        ...this.activities.filter((a) => a.id_MH !== selectedId),
      ];
    } else {
      this.filteredActivities = [...this.activities];
    }
  }

  saveProfileInfo() {
    this._dataService.displayLoading();

    const data = {
      id: this.data.id,
      Description_Place: this.placeInfo.get("Description_Place").value,
      Name_Place: this.placeInfo.get("Name_Place").value,
      Sinpe_Number: this.placeInfo.get("Sinpe_Number").value,
      Code: this.placeInfo.get("Code").value,
    };

    try {
      this.api.updatePlaceMT(data).subscribe({
        next: (response) => {
          if (!response.data.response || response.data.response.error) {
            this._alertService.openSnackBar(
              "Error al guardar la información.",
              "error"
            );
          } else {
            this.store.dispatch(
              new UpdateSelectedPlace(response.data.response)
            );
            this.handler(UpdateSelectedPlace);
            this._alertService.openSnackBar(
              "Se ha actualizado el perfil de la tienda.",
              "success"
            );

            this.updateSelectedActivityPosition();
          }

          this._dataService.displayLoading(false);
        },
        error: (err) => {
          this._alertService.openSnackBar(
            "Error al llamar al API para guardar información.",
            "error"
          );

          this._dataService.displayLoading(false);
        },
      });
    } catch (err) {
      this._alertService.openSnackBar(
        "Error al actualizar el perfil de la tienda.",
        "error"
      );
    }
  }

  updateSelectedActivityPosition() {
    const selectedId = this.placeInfo.controls["Code"].value;
    const selectedActivity = this.activities.find(
      (a) => a.id_MH === selectedId
    );

    if (selectedActivity) {
      this.activities = [
        selectedActivity,
        ...this.activities.filter((a) => a.id_MH !== selectedId),
      ];
      this.filteredActivities = [...this.activities];
    }
  }

  triggerUpload() {
    if (this.fileInput) {
      this.fileInput.nativeElement.click();
    }
  }

  onFileSelected(event: any) {
    const file = event.target.files[0];
    if (!file) return;

    this._fileService.uploadAndUpdatePlacePicture(file).subscribe({
      next: (res) => {
        this.file = res;
        this._alertService.openSnackBar("Foto actualizada correctamente");
      },
      error: () => {
        this._alertService.openSnackBar("Error al actualizar la foto", "error");
      },
    });
  }
}
